<template>
  <div class="sync-page">
    <!-- Header with Logo and Title -->
    <div class="sync-header d-flex align-items-center mb-4">
      <img
        src="@/assets/images/logo/logo.png"
        alt="Logo"
        class="sync-logo mr-3"
      >
      <div>
        <h2 class="mb-0">
          Gestion des Synchronisations
        </h2>
        <small class="text-muted">Gérez la synchronisation des gouvernorats avec les sociétés logistiques externes.</small>
      </div>
      <b-button
        v-b-tooltip.hover
        variant="link"
        class="ml-auto"
        title="Besoin d'aide ?"
        aria-label="Aide"
      >
        <b-icon-info-circle />
      </b-button>
    </div>

    <!-- Search & Filter Bar -->
    <div class="sync-toolbar d-flex mb-3">
      <b-form-input
        v-model="searchQuery"
        placeholder="Rechercher un gouvernorat..."
        class="mr-2"
        type="search"
        aria-label="Recherche"
      />
      <b-form-select
        v-model="statusFilter"
        :options="statusOptions"
        class="mr-2"
        aria-label="Filtrer par statut"
      />
      <b-button
        variant="primary"
        @click="fetchGovernorates"
      >
        <b-icon-arrow-clockwise /> Rafraîchir
      </b-button>
    </div>

    <!-- Toast for Feedback -->
    <b-toast
      v-if="alertVisible"
      :variant="alertVariant"
      :auto-hide-delay="3500"
      solid
      toaster="b-toaster-top-right"
      @hidden="alertVisible = false"
    >
      {{ alertMessage }}
    </b-toast>

    <!-- Loading Overlay -->
    <b-overlay
      :show="isLoading"
      rounded
      opacity="0.2"
    >
      <!-- Governorates Table -->
      <b-table
        :items="filteredGovernorates"
        :fields="tableFields"
        responsive
        hover
        class="sync-table"
        aria-label="Tableau des synchronisations"
      >
        <template #cell(name)="row">
          <div class="d-flex align-items-center">
            <div class="avatar mr-2">
              {{ row.item.name.charAt(0).toUpperCase() }}
            </div>
            <span>{{ row.item.name }}</span>
          </div>
        </template>
        <template #cell(synchronization_status)="row">
          <b-badge :variant="row.item.is_synchronized ? 'success' : 'secondary'">
            {{ row.item.is_synchronized ? 'Synchronisé' : 'Non synchronisé' }}
          </b-badge>
        </template>
        <template #cell(synchronized_companies)="row">
          <span v-if="row.item.synchronized_companies.length">
            <b-badge
              v-for="company in row.item.synchronized_companies"
              :key="company.id"
              variant="info"
              class="mr-1"
            >
              {{ company.name }}
            </b-badge>
          </span>
          <span v-else>-</span>
        </template>
        <template #cell(actions)="row">
          <b-button
            v-b-tooltip.hover
            :title="row.item.is_synchronized ? 'Modifier la synchronisation' : 'Synchroniser'"
            variant="outline-primary"
            size="sm"
            class="mr-1"
            @click="openSynchronizationModal(row.item)"
          >
            <b-icon-pencil />
          </b-button>
          <b-button
            v-if="row.item.is_synchronized"
            v-b-tooltip.hover
            title="Désynchroniser"
            variant="outline-danger"
            size="sm"
            @click="confirmDesync(row.item)"
          >
            <b-icon-x-circle />
          </b-button>
        </template>
      </b-table>
    </b-overlay>

    <!-- Synchronization Modal -->
    <b-modal
      id="synchronization-modal"
      :title="modalTitle"
      ok-title="Synchroniser"
      cancel-title="Annuler"
      ok-variant="primary"
      cancel-variant="secondary"
      aria-label="Synchronisation du gouvernorat"
      @ok="confirmSynchronization"
    >
      <div v-if="selectedGovernorate">
        <div class="mb-3">
          <strong>Gouvernorat :</strong> {{ selectedGovernorate.name }}
        </div>
        <b-form-group label="Société logistique externe">
          <v-select
            v-model="selectedOutsourcingCompany"
            :options="outsourcingCompanies"
            placeholder="Sélectionner une société..."
            label="name"
          >
            <template #option="option">
              <div class="d-flex align-items-center">
                <b-icon-building class="mr-2" />
                <span>{{ option.name }}</span>
              </div>
            </template>
          </v-select>
        </b-form-group>
        <b-alert
          v-if="selectedGovernorate.is_synchronized"
          variant="warning"
          show
          class="mt-2"
        >
          Cette action mettra à jour la synchronisation actuelle.
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  BTable, BBadge, BButton, BModal, BFormGroup, BAlert, BFormInput, BFormSelect, BOverlay, BToast,
  BIconInfoCircle, BIconArrowClockwise, BIconPencil, BIconXCircle, BIconBuilding,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'GovernorateSynchronizationManagement',
  components: {
    vSelect,
    BTable,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BAlert,
    BFormInput,
    BFormSelect,
    BOverlay,
    BToast,
    BIconInfoCircle,
    BIconArrowClockwise,
    BIconPencil,
    BIconXCircle,
    BIconBuilding,
  },
  data() {
    return {
      governorates: [],
      outsourcingCompanies: [],
      selectedGovernorate: null,
      selectedOutsourcingCompany: null,
      isLoading: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      searchQuery: '',
      statusFilter: '',
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'synchronized', text: 'Synchronisé' },
        { value: 'not_synchronized', text: 'Non synchronisé' },
      ],
      tableFields: [
        { key: 'name', label: 'Gouvernorat' },
        { key: 'synchronization_status', label: 'Statut' },
        { key: 'synchronized_companies', label: 'Sociétés synchronisées' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],
    }
  },
  computed: {
    modalTitle() {
      return this.selectedGovernorate
        ? `Synchronisation du gouvernorat : ${this.selectedGovernorate.name}`
        : 'Synchronisation du gouvernorat'
    },
    filteredGovernorates() {
      let filtered = this.governorates
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(gov => gov.name.toLowerCase().includes(query))
      }
      if (this.statusFilter === 'synchronized') {
        filtered = filtered.filter(gov => gov.is_synchronized)
      } else if (this.statusFilter === 'not_synchronized') {
        filtered = filtered.filter(gov => !gov.is_synchronized)
      }
      return filtered
    },
  },
  created() {
    this.fetchOutsourcingCompanies()
    this.fetchGovernorates()
  },
  methods: {
    async fetchOutsourcingCompanies() {
      try {
        this.isLoading = true
        const { data } = await axios.get('api/companies/outsourcing-company/')
        this.outsourcingCompanies = data
      } catch (error) {
        this.showAlert('danger', 'Échec du chargement des sociétés logistiques')
        console.error('Error fetching outsourcing companies:', error)
      } finally {
        this.isLoading = false
      }
    },

    async fetchGovernorates() {
      try {
        this.isLoading = true
        // Fetch all governorates
        const { data: allGovernorates } = await axios.get('api/governorates/')

        // Fetch sync configurations
        const { data: syncConfigs } = await axios.get('api/companies/governorate-syncs/')

        // Map governorates with their sync status
        this.governorates = allGovernorates.map(gov => {
          const syncs = syncConfigs.filter(sync => sync.governorate === gov.id)
          return {
            ...gov,
            is_synchronized: syncs.length > 0 && syncs.some(s => s.is_synchronized),
            synchronized_companies: syncs
              .filter(s => s.is_synchronized)
              .map(s => ({
                id: s.outsourcing_company,
                name: s.outsourcing_company_name,
              })),
          }
        })
      } catch (error) {
        this.showAlert('danger', 'Échec du chargement des gouvernorats')
        console.error('Error fetching governorates:', error)
      } finally {
        this.isLoading = false
      }
    },

    openSynchronizationModal(governorate) {
      this.selectedGovernorate = governorate
      this.selectedOutsourcingCompany = governorate.is_synchronized
        ? governorate.synchronized_companies[0]
        : null

      this.$bvModal.show('synchronization-modal')
    },

    async confirmSynchronization() {
      if (!this.selectedGovernorate || !this.selectedOutsourcingCompany) {
        this.showAlert('warning', 'Veuillez sélectionner une société logistique externe')
        return
      }

      try {
        this.isLoading = true
        await axios.post('api/companies/update-supplier-outsourcing/', {
          governorate_id: this.selectedGovernorate.id,
          outsourcing_company_id: this.selectedOutsourcingCompany.id,
          is_synchronized: true,
        })

        // Refresh governorates to get updated synchronization status
        await this.fetchGovernorates()

        this.showAlert('success', `${this.selectedGovernorate.name} synchronisé avec succès`)

        // Close modal
        this.$bvModal.hide('synchronization-modal')
      } catch (error) {
        const errorMessage = error.response?.data?.error
          || 'Échec de la synchronisation du gouvernorat'

        this.showAlert('danger', errorMessage)
        console.error('Synchronization error:', error)
      } finally {
        this.isLoading = false
      }
    },

    async desynchronizeGovernorate(governorate) {
      try {
        this.isLoading = true
        // Delete all sync configs for this governorate
        await axios.post('api/companies/desynchronize-governorate/', {
          governorate_id: governorate.id,
        })
        await this.fetchGovernorates()
        this.showAlert('success', `${governorate.name} désynchronisé avec succès`)
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Échec de la désynchronisation du gouvernorat'
        this.showAlert('danger', errorMessage)
        console.error('Desynchronization error:', error)
      } finally {
        this.isLoading = false
      }
    },

    confirmDesync(governorate) {
      this.$bvModal.msgBoxConfirm(
        `Voulez-vous vraiment désynchroniser le gouvernorat « ${governorate.name} » ?`,
        {
          title: 'Confirmation',
          size: 'md',
          okVariant: 'danger',
          okTitle: 'Oui',
          cancelTitle: 'Non',
          footerClass: 'p-2',
          centered: true,
        },
      ).then(value => {
        if (value) this.desynchronizeGovernorate(governorate)
      })
    },

    showAlert(variant, message) {
      this.alertVisible = true
      this.alertVariant = variant
      this.alertMessage = message
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
.sync-page {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2rem 2vw;
}
.sync-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
}
.sync-logo {
  height: 48px;
  width: 48px;
  object-fit: contain;
}
.sync-toolbar {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}
.sync-table {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}
.avatar {
  width: 32px;
  height: 32px;
  background: #e3e6ef;
  color: #3a3a3a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
}
</style>
